// AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { apiURL } from './config';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [isSSO, setIsSSO] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const checkTokenExpiration = useCallback(() => {
        const tokenExpireTime = localStorage.getItem("token_expire");
        if (!tokenExpireTime) {
            return true;
        }
        const isExpired = moment().isAfter(moment(tokenExpireTime));
        return isExpired;
    }, []);

    const checkInternalLogin = useCallback(() => {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken && !checkTokenExpiration()) {
            setUser(JSON.parse(storedUser));
            setRole(localStorage.getItem('role'));
            setIsSSO(false);
            return true;
        }
        return false;
    }, [checkTokenExpiration]);

    const checkSSOLogin = useCallback(() => {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken && !checkTokenExpiration() && localStorage.getItem('isSSO') === 'true') {
            setUser(JSON.parse(storedUser));
            setRole('sso_user');
            setIsSSO(true);
            return true;
        }
        return false;
    }, [checkTokenExpiration]);

    const resetAuthState = useCallback(() => {
        setUser(null);
        setRole(null);
        setIsSSO(false);
        // 清除 localStorage 中的認證資訊
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('role');
        localStorage.removeItem('username');
        localStorage.removeItem('token_expire');
        localStorage.removeItem('isSSO');
    }, []);

    useEffect(() => {
        const ssoUnprotectedPaths = ['/sso-login', '/sso-error'];
        if (ssoUnprotectedPaths.includes(location.pathname)) {
            setLoading(false);
            return;
        }

        const checkLoginStatus = async () => {
            let isLoggedIn = false;

            if (location.pathname.startsWith('/sso') && !ssoUnprotectedPaths.includes(location.pathname)) {
                isLoggedIn = checkSSOLogin();
                if (!isLoggedIn) {
                    resetAuthState(); // 清除認證狀態
                    navigate('/sso-chatbot' + location.search, { replace: true });
                }
            } else {
                isLoggedIn = checkInternalLogin();
                if (!isLoggedIn && location.pathname !== '/login' && location.pathname !== '/') {
                    resetAuthState(); // 清除認證狀態
                    navigate('/sso-chatbot', { replace: true });
                }
            }

            if (!isLoggedIn && !location.pathname.endsWith('login') && !ssoUnprotectedPaths.includes(location.pathname)) {
                resetAuthState();
            }
            setLoading(false);
        };

        checkLoginStatus();
    }, [location.pathname, location.search, navigate, checkInternalLogin, checkSSOLogin, resetAuthState]);

    const login = async (email, password) => {
        try {
            const response = await fetch(`${apiURL}api/login`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });
            if (response.ok) {
                const userData = await response.json();
                localStorage.setItem('token', userData.access_token);
                localStorage.setItem('user', JSON.stringify(userData));
                localStorage.setItem('role', userData.role);
                localStorage.setItem('username', userData.user_name);
                localStorage.setItem('token_expire', moment().add(6, 'hour').format());
                setUser(userData);
                setRole(userData.role);
                setIsSSO(false);
                navigate('/chatbot');
            } else {
                throw new Error('登入失敗');
            }
        } catch (error) {
            console.error('AuthContext: Login error:', error);
            throw error;
        }
    };
    
    const ssoLogin = useCallback(async (userData) => {
        setUser(userData);
        setRole('sso_user');
        setIsSSO(true);
        localStorage.setItem('token', userData.access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('role', 'sso_user');
        localStorage.setItem('username', userData.user_name);
        localStorage.setItem('token_expire', moment().add(12, 'hour').toISOString());
        localStorage.setItem('isSSO', 'true');
        return true;
    }, []);

    const logout = useCallback(() => {
        resetAuthState();
        navigate('/sso-chatbot');
    }, [navigate, resetAuthState]);

    const value = {
        user,
        role,
        isSSO,
        loading,
        login,
        ssoLogin,
        logout,
        checkInternalLogin,
        checkSSOLogin
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
